import { parseLocalBroadcastPayload, readLabel } from "./localBroadcastPayload";

/**
 * Traduce el JSON que kero publica en la región de STATUS del control mailbox (broadcast LOCAL,
 * sin WebTransport) al evento que consume la UI. Es la única vía de vuelta kero -> extensión ->
 * renderer: sin ella un fallo de RTMP se quedaba en el log de kero y la app seguía mostrando
 * "emitiendo" indefinidamente.
 *
 * Contrato con kero (`server/local_shm.rs`, `write_status`):
 *   {"state":"rtmp_failed","source":"rtmpsink_0","message":"..."}
 *   {"state":"reconnecting","attempt":1,"max":5}
 *   {"state":"reconnected"}
 *   {"state":"gave_up","message":"..."}
 */
export type LocalBroadcastEvent =
  | {
      kind: "reconnect";
      status: "reconnecting" | "reconnected";
      source: string;
      attempt: number;
      maxAttempts: number;
    }
  | { kind: "fatal"; source: string; message: string }
  | null;

/** Fallback de `source` cuando kero no lo manda (p.ej. en gave_up, que no viene de un sink puntual). */
export const DEFAULT_LOCAL_SOURCE = "RTMP";

export function parseLocalBroadcastStatus(payload: unknown): LocalBroadcastEvent {
  const status = parseLocalBroadcastPayload(payload);

  if (!status) return null;

  const source = readLabel(status.source, DEFAULT_LOCAL_SOURCE);
  const attempt = typeof status.attempt === "number" ? status.attempt : 0;
  const maxAttempts = typeof status.max === "number" ? status.max : 0;

  switch (status.state) {
    // Un fallo puntual del sink todavía no es fatal: kero reintenta. Se avisa igual para que el
    // usuario no vea la app "al aire" en silencio mientras se reconecta.
    case "rtmp_failed":
    case "reconnecting":
      return {
        kind: "reconnect",
        status: "reconnecting",
        source,
        attempt,
        maxAttempts,
      };

    case "reconnected":
      return {
        kind: "reconnect",
        status: "reconnected",
        source,
        attempt,
        maxAttempts,
      };

    // kero agotó los reintentos: la emisión está muerta y hay que cortarla de verdad.
    case "gave_up":
      return {
        kind: "fatal",
        source,
        message: typeof status.message === "string" ? status.message : "",
      };

    default:
      return null;
  }
}
